import * as Sentry from "@sentry/react";
import { Avatar } from "@/components/Avatar";
import { useUrl } from "@/hooks/useUrl.ts";
import { isFeatureFlagOn } from "@/service/featureFlags.ts";
import {
  MessageQuestionSquare,
  Package,
  Send01,
  File06,
  Award03,
  Dataflow03,
  RefreshCw05,
  Grid01,
  ChevronLeftDouble,
} from "@untitled-ui/icons-react";
import Logo from "@/assets/s25-logo-round.svg?react";
import {
  isRouteErrorResponse,
  Link,
  Outlet,
  useLocation,
  useNavigation,
  useParams,
  useRouteError,
} from "react-router";
import { OrganisationType, UserType } from "@/api/user/types.ts";
import { findWorkspace } from "@/loaders.ts";
import { UserProvider } from "./context/UserProvider.tsx";
import { userQuery } from "@/queries.ts";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useEffect, useState, type PropsWithChildren } from "react";
import { NavLink } from "react-router";
import { authUrl, ExpiredError } from "@/api/client.ts";
import { Page } from "./components/Page.tsx";
import notFound from "@/assets/notFound.png";
import { Box, Spacer } from "@supply25/design/layout";
import { Button } from "@supply25/design/button";
import { cn } from "@supply25/design/classname";
import { lsConstants } from "./constants.ts";

export const PublicLayout = () => {
  return (
    <div className="min-h-screen">
      <Outlet />
    </div>
  );
};

export const RootLayout = () => {
  return (
    <UserProvider>
      <WithNav>
        <Outlet />
      </WithNav>
    </UserProvider>
  );
};

export const RootBoundary = () => {
  const error = useRouteError();

  useEffect(() => {
    if (error instanceof ExpiredError) return;
    Sentry.captureException(error);
  }, [error]);

  if (error instanceof ExpiredError) {
    return (
      <div className="z-100 bg-secondary-900/40 backdrop-blur-xs fixed inset-0 flex items-center justify-center">
        <div className="Card grid gap-4 px-24 py-16 text-center leading-none shadow-2xl">
          <h1 className="h2">Your session has expired</h1>
          <p className="text-lg">Please log in to continue.</p>
          <Link
            to={authUrl(
              "/login",
              window.location.pathname + window.location.search
            )}
            // @todo replace with Button after refactor
            className="Button justify-center"
            theme="primary"
          >
            Log in
          </Link>
        </div>
      </div>
    );
  }
  return (
    <UserProvider>
      <WithNav>
        <Page>
          <div className="grid h-full place-content-center place-items-center gap-8 p-8 text-center">
            {isRouteErrorResponse(error) ? (
              <>
                <img src={notFound} alt="" />
                <h1 className="h2">{error.data}</h1>
                <Link to="." className="Button" theme="primary">
                  <Grid01 /> Back to dashboard
                </Link>
              </>
            ) : (
              <>
                <h1>Something went wrong</h1>
                {error instanceof Error && <p>{error.message}</p>}
                <Link to="#" className="Button" theme="primary">
                  <RefreshCw05 /> Try again
                </Link>
              </>
            )}
          </div>
        </Page>
      </WithNav>
    </UserProvider>
  );
};

const WithNav = ({ children }: PropsWithChildren) => {
  const { pathname } = useLocation();
  const showNav =
    !pathname.startsWith("/welcome") && !pathname.startsWith("/organisation");
  return (
    <div className={cn("SideNavLayout", !showNav && "[--width:0]")}>
      {showNav ? <GlobalNav /> : <div />}
      <LoadingBar />
      {children}
    </div>
  );
};

const NAV_ID = "side-nav";

const GlobalNav = () => {
  const { data: user } = useSuspenseQuery(userQuery);
  const { workspaceId } = useParams();
  // These are optional since we can render some of the nav without them
  const { organisation = undefined, workspace = undefined } = workspaceId
    ? findWorkspace(user, workspaceId)
    : {};
  const { buildUrl } = useUrl();

  const showPackages =
    organisation?.organisationType === OrganisationType.BUYER;
  // @TODO Remove isGuest later as we should be checking not hiding
  const isGuest = organisation?.userType === UserType.GUEST;
  const showAssessments =
    !isGuest &&
    organisation?.organisationType === OrganisationType.BUYER &&
    workspace?.features?.createAssessment === true;
  const showSubmissions =
    !isGuest && organisation?.organisationType === OrganisationType.SUPPLIER;
  const showFaq = Boolean(workspaceId);
  const showContracts =
    !isGuest && isFeatureFlagOn("contractV2") && organisation && workspace;
  const showRiskMap =
    !isGuest &&
    isFeatureFlagOn("riskMap") &&
    organisation?.organisationType === OrganisationType.BUYER &&
    Boolean(workspace);
  return (
    <nav id={NAV_ID} className="SideNav">
      <Box className="h-full">
        <Box gap="2xl" divider="dim">
          {organisation && workspace && (
            <Box>
              <NavLink to={buildUrl(`organisation/${organisation.id}`)}>
                <Avatar roundedSquare name={organisation.name} />{" "}
                <span className="truncate">{organisation.name}</span>
              </NavLink>
              <NavLink to={buildUrl(`organisation/${organisation.id}`)}>
                <Avatar name={workspace.name} />
                <span className="truncate">{workspace.name}</span>
              </NavLink>
            </Box>
          )}
          <Box>
            {showPackages && (
              <NavLink to={buildUrl("packages")}>
                <Package /> Packages
              </NavLink>
            )}
            {showAssessments && (
              <NavLink to={buildUrl("assessments")}>
                <File06 /> Assessments
              </NavLink>
            )}
            {showContracts && (
              <NavLink to={buildUrl("contracts")}>
                <Award03 /> Contracts
              </NavLink>
            )}
            {showRiskMap && (
              <NavLink to={buildUrl("risk-map")}>
                <Dataflow03 /> Risk Map
              </NavLink>
            )}
            {showSubmissions && (
              <NavLink to={buildUrl("applications")}>
                <Send01 /> Submissions
              </NavLink>
            )}
            {showFaq && (
              <NavLink to={buildUrl("faq")}>
                <MessageQuestionSquare /> FAQ
              </NavLink>
            )}
          </Box>
        </Box>
        <Spacer />
        <Box gap="sm">
          <NavButton />
          <NavLink to="/" data-logo>
            <Logo width={40} height={40} />
          </NavLink>
        </Box>
      </Box>
    </nav>
  );
};

const LoadingBar = () => {
  const navigation = useNavigation();
  const isLoading = navigation.state === "loading";
  if (!isLoading) return null;
  return (
    <div className="animate-loading-bar bg-accent fixed left-0 right-0 top-0 h-1 origin-left" />
  );
};

const KEY = lsConstants.NAV_EXPANDED;

const NavButton = () => {
  const [open, setOpen] = useState(() => {
    const stored = localStorage.getItem(KEY);
    return stored ? JSON.parse(stored) : true;
  });
  useEffect(() => {
    if (open) localStorage.setItem(KEY, "true");
    else localStorage.setItem(KEY, "false");
  }, [open]);
  return (
    <Button
      id="nav-button"
      aria-controls={NAV_ID}
      aria-expanded={open}
      onClick={() => setOpen(!open)}
      className="hover:bg-secondary-50 self-start p-2"
      label="Toggle navigation"
      icon={
        <ChevronLeftDouble className="in-aria-expanded:rotate-180 transition-transform" />
      }
    />
  );
};
